// Generates an assignment YAML of type v1_order with `unit: letter` — the
// spell-the-word runner (#152): the student is shown the Ukrainian word and
// assembles the English one out of shuffled letter tiles.
//
// The Ukrainian side goes into `meta.translation`, which is the student's ONLY
// clue — the runner never shows the English word (that is the answer). A line
// with no ` - ` translation is therefore useless here and is skipped.
//
// converts a text like:
// cat - кіт
// ice cream - морозиво      <- skipped, see SKIPPED below
//
// to:
// version: 1
// type: order
// unit: letter
// name: Topic spelling
// level: A1
// questions:
//   - meta:
//       text: Spell the word
//       translation: кіт
//       assignment:
//     variants:
//       - t
//       - c
//       - a
//     correct: cat
//
// SKIPPED entries — `unit: letter` joins the placed tiles with an empty
// string, so anything that is not a single unbroken token cannot be spelled:
// multi-word phrases ("ice cream"), and entries with no translation. Long
// words are skipped too — a bank of 13+ tiles does not fit a phone and stops
// being a spelling exercise. Everything skipped here still flows into the
// other generators on the page untouched.

import { yamlScalar } from './yaml'

// Longest word we will ask a child to assemble tile by tile.
const MAX_LETTERS = 12

// Letters plus the two punctuation marks that legitimately live inside an
// English word ("don't", "T-shirt"). They become their own tile, which is
// fine — what we cannot have is whitespace.
const SPELLABLE = /^[\p{L}'-]+$/u

const shuffled = (list) => {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

// A shuffle that lands back on the original spelling hands the student a free
// point, so retry a few times. Bounded, because a word like "aaa" can never
// scramble and must not spin forever.
const scrambled = (word) => {
  const letters = [...word]
  for (let attempt = 0; attempt < 8; attempt++) {
    const candidate = shuffled(letters)
    if (candidate.join('') !== word) {
      return candidate
    }
  }
  return letters
}

export function wordSpellingGenerator(rawText, topic, levelString, limit) {
  const level = levelString.toUpperCase()

  const words = rawText.split('\n').map((line) => {
    const [word, translation] = line.split(' - ')
    if (!word || !translation) {
      return null
    }
    const spelling = word.trim()
    if (!SPELLABLE.test(spelling) || spelling.length > MAX_LETTERS) {
      return null
    }
    return { word: spelling, translation: translation.trim() }
  }).filter((w) => w)

  const picked = shuffled(words).slice(0, limit || words.length)

  // Every line was a phrase / too long / untranslated — say nothing rather
  // than emit a headed YAML with an empty `questions` list, which the backend
  // validator would reject anyway.
  if (!picked.length) {
    return null
  }

  const questions = picked.map((entry) => `  - meta:
      text: Spell the word
      translation: ${yamlScalar(entry.translation)}
      assignment:
    variants:${scrambled(entry.word).map((letter) => `\n      - ${yamlScalar(letter)}`).join('')}
    correct: ${yamlScalar(entry.word)}
`).join('')

  return `version: 1
type: order
unit: letter
name: ${yamlScalar(`${topic} spelling`)}
level: ${level}
questions:
${questions}`
}
